import { nextTierFor, tierForXp } from '@shared';
import { useGame } from '../../game/store';
import { formatCreditAmount, formatCredits } from '../../lib/format';
import { Icon } from '../ui/Icon';
import styles from './StatusNudge.module.css';

/**
 * Where the player stands, and how far the next status is.
 *
 * The distance is the whole point. A bare status badge says "you are Silver",
 * which is a fact; "220 XP to Gold" is a *gap*, and a gap is what makes someone
 * close it. It sits on Home, above everything else, because it is also the one
 * line a chatter can quote in a DM.
 *
 * Reads the same shared helpers as the server, over the projected track, so the
 * threshold shown here and the one the ladder settles on cannot drift.
 */
export function StatusNudge({ onOpenProfile }: { onOpenProfile?: () => void }) {
  const { progression, naming } = useGame();

  const ladder = { tiers: progression.track };
  const tier = tierForXp(ladder, progression.xp);
  const next = nextTierFor(ladder, progression.xp);
  const boost = progression.boost;

  const span = next ? next.xp - tier.xp : 1;
  const done = next ? progression.xp - tier.xp : 1;
  const percent = Math.max(0, Math.min(100, Math.round((done / span) * 100)));

  /** What the next tier hands over, in one short line. */
  const reward = next
    ? [
        next.creditBonus > 0 ? `+${formatCreditAmount(naming, next.creditBonus)}` : null,
        next.couponPercent > 0 ? `${next.couponPercent}% coupon` : null,
        ...next.perks,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <section className={styles.card}>
      <div className={styles.head}>
        <span className={styles.tier}>
          <Icon name="trophy" size={14} />
          {tier.name}
        </span>
        {boost && (
          <span className={styles.boost}>
            <Icon name="bolt" size={12} />
            {boost.label} — {naming.xpName} ×{boost.multiplier}
          </span>
        )}
        {onOpenProfile && (
          <button type="button" className={styles.link} onClick={onOpenProfile}>
            View status
          </button>
        )}
      </div>

      {next ? (
        <>
          <div className={styles.gap}>
            <strong className={styles.amount}>
              {formatCredits(next.xp - progression.xp)} {naming.xpName}
            </strong>{' '}
            to <strong>{next.name}</strong>
          </div>
          <div className={styles.bar}>
            <span className={styles.fill} style={{ width: `${percent}%` }} />
          </div>
          {reward && <p className={styles.reward}>Unlocks {reward}</p>}
        </>
      ) : (
        <div className={styles.gap}>
          <strong className={styles.amount}>
            {formatCredits(progression.xp)} {naming.xpName}
          </strong>{' '}
          — the top of the ladder.
        </div>
      )}
    </section>
  );
}
